import SearchIcon from "@redotech/redo-icons/search-md.svg";
import { RedoCommandShortcut } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-shortcut";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { getShortcutDisplayParts } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import { Text } from "@redotech/redo-web/text";
import { Fragment, memo, useMemo, useState } from "react";
import * as styles from "./keyboard-shortcuts-modal.module.css";
import {
  SHORTCUT_CATEGORIES,
  ShortcutCategory,
  ShortcutEntry,
} from "./shortcut-catalog";

// Words a merchant might type to find a modifier, so "cmd", "ctrl", "option",
// etc. all match the corresponding shortcuts.
const MODIFIER_SEARCH_WORDS: Record<string, string[]> = {
  meta: ["cmd", "command", "ctrl", "control"],
  ctrl: ["ctrl", "control"],
  alt: ["alt", "option", "opt"],
  shift: ["shift"],
};

/** Lowercased text a row is matched against: action, category, and its keys. */
function entryHaystack(entry: ShortcutEntry, categoryLabel: string): string {
  const keyBits = entry.shortcuts.flatMap((shortcut) => [
    shortcut.keyLabel,
    ...(shortcut.modifiers ?? []).flatMap(
      (modifier) => MODIFIER_SEARCH_WORDS[modifier] ?? [],
    ),
  ]);
  return [entry.description, categoryLabel, ...keyBits].join(" ").toLowerCase();
}

export const KeyboardShortcutsModal = memo(function KeyboardShortcutsModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");

  const query = search.trim().toLowerCase();

  // Filter every category's rows by the text query (matches action, key, or
  // category name); an empty query shows everything.
  const visibleCategories = useMemo(() => {
    if (!query) {
      return SHORTCUT_CATEGORIES;
    }
    return SHORTCUT_CATEGORIES.map((category) => ({
      ...category,
      entries: category.entries.filter((entry) =>
        entryHaystack(entry, category.label).includes(query),
      ),
    })).filter((category) => category.entries.length > 0);
  }, [query]);

  return (
    <RedoModal
      hideFooter
      isOpen={isOpen}
      mainContentClassName={styles.body}
      modalSize="xl"
      onModalCloseRequested={onClose}
      subtitle="Single keys may require the associated menu to be open. ⌘ shortcuts use Ctrl on Windows and Linux."
      title="Keyboard shortcuts"
    >
      <Flex dir="column" gap="lg">
        <RedoTextInput
          fullWidth
          IconLeading={SearchIcon}
          placeholder="Search by action, key, or category"
          setValue={setSearch}
          value={search}
        />
        <div className={styles.shortcutList}>
          {visibleCategories.length === 0 ? (
            <Text fontSize="sm" textColor="secondary">
              No shortcuts match “{search}”
            </Text>
          ) : (
            <div className={styles.shortcutColumns}>
              {visibleCategories.map((category) => (
                <ShortcutSection category={category} key={category.key} />
              ))}
            </div>
          )}
        </div>
      </Flex>
    </RedoModal>
  );
});

const ShortcutSection = memo(function ShortcutSection({
  category,
}: {
  category: ShortcutCategory;
}) {
  return (
    <Flex className={styles.shortcutSection} dir="column" gap="sm" mb="4xl">
      <Text
        className={styles.sectionHeader}
        fontSize="xxs"
        fontWeight="semibold"
        textColor="tertiary"
      >
        {category.label}
      </Text>
      <Flex dir="column" gap="none">
        {category.entries.map((entry) => (
          <ShortcutRow entry={entry} key={entry.description} />
        ))}
      </Flex>
    </Flex>
  );
});

const ShortcutRow = memo(function ShortcutRow({
  entry,
}: {
  entry: ShortcutEntry;
}) {
  // Collapse combos that display identically (e.g. the same keys bound in more
  // than one scope) so a row never shows the same chip twice.
  const seenCombos = new Set<string>();
  const keyGroups = entry.shortcuts
    .map((shortcut) => getShortcutDisplayParts(shortcut))
    .filter((parts) => {
      const combo = parts.join("+");
      if (seenCombos.has(combo)) {
        return false;
      }
      seenCombos.add(combo);
      return true;
    });

  return (
    <Flex align="center" gap="md" justify="space-between" py="sm">
      <Text fontSize="sm" textColor="primary">
        {entry.description}
      </Text>
      <Flex align="center" gap="sm" shrink={0}>
        {keyGroups.map((parts, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <Text fontSize="xs" textColor="secondary">
                or
              </Text>
            )}
            <Flex align="center" gap="xs">
              {parts.map((part, partIndex) => (
                <RedoCommandShortcut
                  background
                  hotkey={[part]}
                  key={partIndex}
                  size="md"
                />
              ))}
            </Flex>
          </Fragment>
        ))}
      </Flex>
    </Flex>
  );
});
